import { Box, Button, Divider, Typography } from "@mui/material";
import { KOZYCLANLOGO } from "assets/index";
import { Link } from "react-router";
import useSignInLogic from "./hooks/useSignInLogic";
import LoginInputField from "./signInFields/login-input";
import PasswordInput from "./signInFields/password-input";
import UserTypeInput from "./signInFields/user-type-input";
import { Business, Group } from "@mui/icons-material";

const SignInScreen = () => {
  const { formik, isPasswordEnabled, isChecking, isLoading, handleSubmit } =
    useSignInLogic();

  const linkStyle = {
    color: "#616161",
    textDecoration: "underline",
    fontFamily: "Inter",
    fontSize: "14px",
    whiteSpace: "nowrap",
    transition: "color 0.2s ease",
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        px: 1,
        alignItems: { xs: "center", md: "flex-start" },
        gap: 2,
      }}
      justifyContent="center"
    >
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          justifyContent: "center",
          mb: 1,
        }}
      >
        <img src={KOZYCLANLOGO} width={140} alt="Kozyclan logo" />
      </Box>

      {/* only for desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
        }}
      >
        <img src={KOZYCLANLOGO} width={140} alt="Kozyclan logo" />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-around",
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          gap: { xs: 4, md: 3 },
          py: { xs: 2, md: 4 },
        }}
      >
        <Box
          flex={1}
          sx={{
            width: "100%",
            textAlign: "center",
            maxWidth: { xs: "100%", md: "650px" },
            p: { xs: 3, sm: 4 },
            borderRadius: { xs: 2, md: 3 },
            boxShadow: {
              xs: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              md: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            },
            mx: { xs: 0, md: 2 },
            mb: { xs: 0, md: 0 },
            bgcolor: "background.paper",
            height: { md: "660px", lg: "660px" },
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            transition: "box-shadow 0.3s ease",
            "&:hover": {
              boxShadow: { md: "0px 6px 25px rgba(0, 0, 0, 0.1)" },
            },
            //hide scrollbar
            "&::-webkit-scrollbar": {
              width: "0.4em",
            },
          }}
        >
          <Typography
            variant="h4"
            fontWeight="bold"
            color="primary.main"
            mb={3}
            sx={{
              lineHeight: 1.3,
              fontSize: { xs: "1.75rem", sm: "2rem", md: "2.125rem" },
            }}
          >
            Welcome to the Kozyclan Partner Dashboard!
          </Typography>

          <Typography
            variant="body1"
            color="text.primary"
            mb={3}
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem" },
              lineHeight: 1.6,
              opacity: 0.9,
            }}
          >
            Kozyclan envisions a world where travel isn't just about visiting
            places— it's about shared experiences, meaningful connections, and
            hassle-free exploration. Our group travel marketplace brings
            together <strong>trusted merchants</strong> and{" "}
            <strong>passionate hosts</strong> to curate seamless adventures,
            ensuring every trip becomes an unforgettable social experience.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="h5"
            fontWeight="bold"
            color="text.primary"
            mb={3}
          >
            User Roles
          </Typography>

          <Box
            textAlign="left"
            mb={4}
            sx={{
              "& > div": {
                transition: "transform 0.2s ease",
                "&:hover": {
                  transform: "translateX(4px)",
                },
              },
            }}
          >
            <Box display="flex" alignItems="flex-start" mb={2.5} gap={1.5}>
              <Business fontSize="small" color="primary" sx={{ mt: 0.5 }} />
              <Typography variant="body1" color="text.primary">
                <strong>Merchant:</strong> The seller/vendor offering trips to
                Kozyclan users and fulfilling the promised services.
              </Typography>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <Group fontSize="small" color="primary" sx={{ mt: 0.5 }} />
              <Typography variant="body1" color="text.primary">
                <strong>Host:</strong> The trip leader responsible for ensuring
                memorable journeys, fostering connections, and enhancing the
                overall experience.
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body1"
            color="primary"
            fontWeight="bold"
            sx={{
              p: 2.5,
              borderRadius: 2,
              display: "inline-block",
              fontStyle: "italic",
              bgcolor: "primary.lightest",
              border: "1px dashed",
              borderColor: "primary.light",
              mt: "auto",
              textAlign: "center",
            }}
          >
            We're excited to build amazing experiences together!
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4 },
            width: "100%",
            maxWidth: { xs: "100%", md: "650px" },
            borderRadius: { xs: 2, md: 3 },
            bgcolor: "secondary.light",
            boxShadow: {
              xs: "0px 2px 8px rgba(0, 0, 0, 0.05)",
              md: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            },
            display: "flex",
            flexDirection: "column",
            height: { md: "660px", lg: "660px" },
            gap: 2.5,
            transition: "box-shadow 0.3s ease",
            "&:hover": {
              boxShadow: { md: "0px 6px 25px rgba(0, 0, 0, 0.1)" },
            },
            mx: { xs: 0, md: 2 },
          }}
        >
          <Typography
            color="text.primary"
            variant="h5"
            mb={2}
            fontWeight="medium"
            sx={{
              fontSize: { xs: "1.5rem", sm: "1.75rem" },
            }}
          >
            Login Account
          </Typography>

          <UserTypeInput formik={formik} />

          <LoginInputField
            formik={formik}
            title="Mobile Number or Email-Id"
            placeholder="Enter Mobile Number or Email-Id"
            handleSubmit={handleSubmit}
          />

          {isPasswordEnabled && <PasswordInput formik={formik} />}

          <Link to="/forget-password" style={{ alignSelf: "flex-start" }}>
            <Typography
              color="text.secondary"
              fontSize={14}
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                transition: "color 0.2s ease",
              }}
            >
              Forgot Password?
            </Typography>
          </Link>

          <Button
            disabled={isChecking}
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#000",
              p: { xs: 1.5, md: 1.8 },
              textTransform: "capitalize",
              fontSize: { xs: 16, md: 18 },
              my: 2,
              borderRadius: 1.5,
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              "&:hover": {
                backgroundColor: "#222",
                transform: "translateY(-2px)",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
              },
              "&:active": {
                transform: "translateY(1px)",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.2)",
              },
            }}
            variant="contained"
          >
            {isPasswordEnabled
              ? "Login"
              : isChecking
              ? "Checking..."
              : "Proceed"}
          </Button>

          <Link
            to="/"
            style={{
              textAlign: "center",
              margin: "40px 0 80px 0",
              textDecoration: "none",
            }}
          >
            <Typography
              sx={{
                color: "text.secondary",
                textDecoration: "underline",
                transition: "color 0.2s ease",
              }}
            >
              Don't have an account? Create now.
            </Typography>
          </Link>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          py: { xs: 2, md: 3 },
          px: { xs: 2, md: 4 },
          borderTop: "1px solid #E0E0E0",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "center",
          alignItems: "center",
          bgcolor: "background.paper",
          gap: { xs: 2, sm: 3 },
          textAlign: "center",
          zIndex: 1,
          mt: "auto",
        }}
      >
        <Typography
          variant="body2"
          color="#616161"
          sx={{ fontSize: { xs: "12px", sm: "14px" } }}
        >
          Copyright © 2025 Kashika1 Technology Pvt Ltd. All Rights Reserved.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: { xs: 2, sm: 3 },
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <a
            href="https://kozyclan.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...linkStyle,
            }}
          >
            Kozyclan Website
          </a>
          <a
            href="https://kozyclan.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...linkStyle,
            }}
          >
            Terms of Service
          </a>
          <a
            href="https://kozyclan.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...linkStyle,
            }}
          >
            Privacy Policy
          </a>
        </Box>
      </Box>
    </Box>
  );
};

export default SignInScreen;
